// ============================================================================
// Hooks TanStack Query — GESTION D'EMPLOYE (L09) : hub + 28 mutations
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (M-750/M-158) : le SPA consomme `can.*`/`viewer.*` pour les affordances, il ne
// DECIDE jamais (lecon GA-05). Un 403 serveur devient un message, pas un crash.
//
// Le serveur EST FAIT (merge 00d6225c) : ce module ne fait que CONSOMMER le contrat
// FIGE de /api/management/* (endpoints + DTO lus dans server/routes/management.ts,
// jamais devines). Les types ci-dessous MIROITENT les DTO serveur a l'identique.
//
// Matrice §G (M-816) : le DTO gestion (ManagementCompensationRecord) porte
// offGridReasonPrivate / roleFit* — c'est LA vue gestion. Ces champs ne DOIVENT
// JAMAIS etre rendus sur une surface atteignable par l'employe. La vue self du
// Profil (ProfileCompensation) ne les porte pas, par construction serveur.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import type { OvertimeRule } from '../lib/salary/special-hours';

// ─── Types — MIROIR EXACT des DTO serveur (server/repositories/*.ts) ──────────

export type GestManagementRole = 'admin' | 'manager' | 'director' | 'employee';

export interface ManagementTargetDto {
  email: string;
  fullName: string | null;
  teamId: string | null;
  teamName: string | null;
  managerEmail: string | null;
  isEmployed: boolean;
  teamDirectorEmail: string | null;
  teamDirectorName: string | null;
}

export interface GridEchelon {
  echelon: number;
  rate: number;
  pctFromPrev: number | null;
}
export interface GridNiveau {
  level: number;
  label: string;
  echelonMax: number | null;
}
export interface GridClass {
  id: string;
  label: string;
  sortOrder: number;
  augGrillePct: number | null;
  baseHoursPerWeek: number;
  weeksPerYear: number;
  echelons: GridEchelon[];
  niveaux: GridNiveau[];
}

/**
 * Fiche compensation, VUE GESTION. offGridReasonPrivate / roleFit* = §G, vue
 * gestion UNIQUEMENT — jamais rendus sur une surface atteignable par l'employe.
 */
export interface ManagementCompensationRecord {
  classId: string | null;
  currentEchelon: number;
  currentNiveau: number | null;
  hireDate: string | null;
  currentHourlyRate: number | null;
  currentRateEffectiveDate: string | null;
  departureDate: string | null;
  offGrid: boolean;
  offGridReasonPublic: string | null;
  offGridReasonPrivate: string | null;
  roleFitFlag: boolean;
  roleFitNote: string | null;
  insuranceBiweekly: number | null;
  cellAllowanceBiweekly: number | null;
  otherAllowanceBiweekly: number | null;
  hasCompanyVehicle: boolean;
}

export interface RateChange {
  id: string;
  hourlyRate: number;
  effectiveDate: string;
  reason: string;
  note: string | null;
}
export interface SalaryHistoryEntry {
  year: number;
  role: string | null;
  annual: number;
  note: string | null;
}
export interface BonusEntry {
  year: number;
  /**
   * P-ADM-9 / M-166 : FRACTIONS 0..1 en lecture (BD / getManagementCompensationView).
   * L'UI affiche `* 100` (points 0..100) et PUT /bonus renvoie des POINTS —
   * la route divise par 100 avant upsertBonus. JAMAIS un facteur 100 fantome
   * supplementaire cote client au-dela de ce contrat.
   */
  targetPct: number;
  actualPct: number;
  note: string | null;
}
export interface EmployeeWork {
  weeklyHours: number | null;
  mainTasks: string[];
}
export interface HoursOverride {
  id: string;
  startDate: string;
  endDate: string;
  weeklyHours: number;
  reason: string;
}
export interface RatePremium {
  id: string;
  startDate: string;
  endDate: string;
  premiumPerHour: number;
  reason: string;
}
export interface EmployeeContract {
  id: string;
  title: string;
  status: string;
  createdAt: string;
}

export interface ManagementCompensationView {
  record: ManagementCompensationRecord | null;
  classes: GridClass[];
  gridYear: number;
  rateHistory: RateChange[];
  salaryHistory: SalaryHistoryEntry[];
  bonusList: BonusEntry[];
  work: EmployeeWork | null;
  overrides: HoursOverride[];
  premiums: RatePremium[];
  contracts: EmployeeContract[];
  vehicleCostBiweekly: number | null;
  /** ADR-088 regle 5 : seuil et majoration des heures supplementaires de l annee (catalogue). */
  overtimeRule: OvertimeRule;
}

export interface Evaluation {
  id: string;
  year: number;
  score: number;
  passed: boolean;
  applied: boolean;
  status: string;
  comments: string | null;
  criteria: { key: string; label: string; score: number }[];
}
export interface EvalConfig {
  passThreshold: number;
  maxScore: number;
  criteria: { key: string; label: string; weight: number }[];
}
export interface CareerPlan {
  id: string;
  kind: string;
  title: string;
  status: string;
  milestones: { id: string; title: string; done: boolean; targetDate: string | null; sortOrder: number }[];
}
export interface EmployeeObjective {
  id: string;
  year: number;
  period: string;
  title: string;
  status: string;
  targetDate: string | null;
}
export interface EmployeeCheckin {
  id: string;
  year: number;
  quarter: number;
  note: string | null;
  answers: { key: string; label: string; rating: number }[];
}
export interface EmployeeDocument {
  id: string;
  kind: string;
  fileName: string;
  sizeBytes: number;
}
export interface PendingInactivation {
  id: string;
  requestedAt: string;
  requestedByName: string | null;
  lastActiveDay: string;
  reason: string;
  status: string;
}
export interface ContractTemplate {
  id: string;
  name: string;
}
export interface ManagerOption {
  email: string;
  fullName: string | null;
}
export interface MemberPulse {
  period: string;
  workloadScore: number;
  energyScore: number;
  comment: string | null;
}
export interface EmployeeStrengths {
  gallupStrengths: string[];
  nova: { red: number | null; yellow: number | null; green: number | null; blue: number | null };
}

/** PDR-MGT-06 : agregat masse equipe — JAMAIS de ligne individuelle d'autrui. */
export interface TeamPayrollAggregateDto {
  total: number;
  unpricedCount: number;
}

export interface ManagementResponse {
  target: ManagementTargetDto;
  compensation: ManagementCompensationView;
  /** Agregat engine complet sous le meme grant manage — null si cible sans equipe. */
  teamPayroll: TeamPayrollAggregateDto | null;
  evaluations: Evaluation[];
  evalConfig: EvalConfig | null;
  plans: CareerPlan[];
  objectives: EmployeeObjective[];
  checkins: EmployeeCheckin[];
  documents: EmployeeDocument[];
  pendingInactivation: PendingInactivation | null;
  templates: ContractTemplate[];
  rosterOptions: ManagerOption[];
  strengths: EmployeeStrengths | null;
  pulses: MemberPulse[];
  can: {
    setHourlyRate: boolean;
    writeEvaluation: boolean;
    requestInactivation: boolean;
    manageDocuments: boolean;
  };
  documentsUploadDeferred: boolean;
  viewer: { role: GestManagementRole; email: string };
  year: number;
}

// ─── Query (hub) ──────────────────────────────────────────────────────────────

const key = (email: string) => ['gest', 'management', email.toLowerCase()] as const;

/** F-750 : bundle du hub (1 requete). 403/404 = etats stables (pas de retry). */
export function useManagement(email: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: key(email ?? ''),
    queryFn: () => api.get<ManagementResponse>(`/api/management/${encodeURIComponent(email ?? '')}`),
    enabled: enabled && !!email,
    retry: false,
  });
}

// ─── Codes d'erreur metier (GA-21 : jamais un code brut a l'ecran) ───────────

/**
 * Extrait le code metier VERBATIM renvoye par le serveur (errorHandler `legacy` ->
 * `{ error, code, message, ... }`), sinon retombe sur un code generique par statut.
 * Le SPA mappe ensuite ce code sur un message i18n `gest:management.errors.<code>`.
 * Codes serveur connus : off_grid_reason, niveau_invalid, echelon_range,
 * echelon_niveau, past_only, invalid_period, override_overlap, premium_overlap,
 * inactivation_pending, manager_team, manager_not_found, manager_self,
 * manager_cycle, duplicate_strength, invalid_strengths, forbidden, notfound, invalid.
 */
export function managementErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (typeof code === 'string') {
    if (code === 'GEST_UPLOAD_TOO_LARGE') return 'too_large';
    if (code === 'GEST_UPLOAD_TYPE_REFUSED' || code === 'GEST_UPLOAD_TYPE_MISMATCH') return 'type_refused';
    if (code === 'GEST_UPLOAD_QUOTA') return 'quota';
    if (code === 'GEST_UPLOAD_EMPTY' || code === 'GEST_UPLOAD_INVALID') return 'invalid';
    if (code.length > 0 && !code.startsWith('GEST_')) return code;
  }
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

// ─── Mutations ────────────────────────────────────────────────────────────────
// Chaque ecriture invalide le hub gestion ET les vues profil de la cible : une
// ecriture sur un employe rafraichit toutes les surfaces qui le lisent (parite
// serveur `revalidateEmployee`). L'appelant passe l'email de la cible.

interface WithEmail {
  email: string;
}

function useManagementMutation<TPayload>(
  send: (email: string, payload: TPayload) => Promise<unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WithEmail & { payload: TPayload }) => send(input.email, input.payload),
    onSuccess: (_data, input) => {
      // Famille gestion (hub /profile?tab=gestion) + profil/org.
      void qc.invalidateQueries({ queryKey: key(input.email) });
      void qc.invalidateQueries({ queryKey: ['gest', 'profile'] });
      void qc.invalidateQueries({ queryKey: ['gest', 'org'] });
      // AD-D : l'onglet Admin Remuneration consomme aussi ce hub — invalider
      // la famille admin pour que les bundles AD-B/AD-C restent coherents.
      void qc.invalidateQueries({ queryKey: ['gest', 'admin'] });
      void qc.invalidateQueries({ queryKey: ['gest', 'grille-ce'] });
    },
  });
}

const enc = (email: string) => encodeURIComponent(email.toLowerCase().trim());

// F-176
export interface ManagerPayload { managerEmail: string | null }
export const useSetManager = () =>
  useManagementMutation<ManagerPayload>((email, p) => api.put(`/api/management/${enc(email)}/manager`, p));

// F-177 — LIEU de saisie off_grid / role_fit (M-752/754)
export interface CompensationPayload {
  classId: string;
  currentEchelon: number | null;
  currentNiveau: number | null;
  hireDate: string | null;
  offGrid: boolean;
  offGridReasonPrivate: string | null;
  offGridReasonPublic: string | null;
  roleFitFlag: boolean;
  roleFitNote: string | null;
}
export const useSetCompensation = () =>
  useManagementMutation<CompensationPayload>((email, p) => api.put(`/api/management/${enc(email)}/compensation`, p));

// F-178
export interface EvaluationPayload {
  year: number;
  comments: string | null;
  status: 'draft' | 'submitted';
  criteriaScores: Record<string, number>;
  score?: number;
}
export const useSubmitEvaluation = () =>
  useManagementMutation<EvaluationPayload>((email, p) => api.post(`/api/management/${enc(email)}/evaluation`, p));

// F-179
export interface InactivationPayload { lastActiveDay: string; reason: string }
export const useRequestInactivation = () =>
  useManagementMutation<InactivationPayload>((email, p) => api.post(`/api/management/${enc(email)}/inactivation`, p));

// F-180
export interface ContractPayload { templateId: string | null; title: string; body: string | null }
export const useIssueContract = () =>
  useManagementMutation<ContractPayload>((email, p) => api.post(`/api/management/${enc(email)}/contract`, p));
export const useUpdateContractStatus = () =>
  useManagementMutation<{ contractId: string; status: string }>((email, p) =>
    api.patch(`/api/management/${enc(email)}/contract/${p.contractId}`, { status: p.status }));

// F-181
export interface CareerPlanPayload { kind: 'pip' | 'parcours'; title: string; status?: string; milestones: string | null }
export const useCreateCareerPlan = () =>
  useManagementMutation<CareerPlanPayload>((email, p) => api.post(`/api/management/${enc(email)}/career-plan`, p));
export const useUpdateCareerPlanStatus = () =>
  useManagementMutation<{ planId: string; status: string }>((email, p) =>
    api.patch(`/api/management/${enc(email)}/career-plan/${p.planId}`, { status: p.status }));
export const useDeleteCareerPlan = () =>
  useManagementMutation<{ planId: string }>((email, p) =>
    api.delete(`/api/management/${enc(email)}/career-plan/${p.planId}`));

// F-182
export interface WorkPayload { weeklyHours: number | null; mainTasks: string | null }
export const useSetWork = () =>
  useManagementMutation<WorkPayload>((email, p) => api.put(`/api/management/${enc(email)}/work`, p));

// F-183
export interface HoursOverridePayload { startDate: string; endDate: string; weeklyHours: number; reason: string }
export const useAddHoursOverride = () =>
  useManagementMutation<HoursOverridePayload>((email, p) => api.post(`/api/management/${enc(email)}/hours-override`, p));
export const useRemoveHoursOverride = () =>
  useManagementMutation<{ overrideId: string }>((email, p) =>
    api.delete(`/api/management/${enc(email)}/hours-override/${p.overrideId}`));

// F-184
export interface RatePremiumPayload { startDate: string; endDate: string; premiumPerHour: number; reason: string }
export const useAddRatePremium = () =>
  useManagementMutation<RatePremiumPayload>((email, p) => api.post(`/api/management/${enc(email)}/rate-premium`, p));
export const useRemoveRatePremium = () =>
  useManagementMutation<{ premiumId: string }>((email, p) =>
    api.delete(`/api/management/${enc(email)}/rate-premium/${p.premiumId}`));

// F-185
export interface BenefitsPayload {
  insuranceBiweekly: number | null;
  cellAllowanceBiweekly: number | null;
  otherAllowanceBiweekly: number | null;
  hasCompanyVehicle: boolean;
}
export const useSetBenefits = () =>
  useManagementMutation<BenefitsPayload>((email, p) => api.put(`/api/management/${enc(email)}/benefits`, p));

// F-186 — % UI 0-100 (le serveur divise par 100)
export interface BonusPayload { year: number; targetPct: number; actualPct: number; note: string | null }
export const useSetBonus = () =>
  useManagementMutation<BonusPayload>((email, p) => api.put(`/api/management/${enc(email)}/bonus`, p));
export const useDeleteBonus = () =>
  useManagementMutation<{ year: number }>((email, p) =>
    api.delete(`/api/management/${enc(email)}/bonus/${p.year}`));

// F-187 — taux reel (canSetHourlyRate)
export interface RatePayload { hourlyRate: number; effectiveDate: string; reason: string; note: string | null }
export const useAddRate = () =>
  useManagementMutation<RatePayload>((email, p) => api.post(`/api/management/${enc(email)}/rate`, p));
export const useDeleteRate = () =>
  useManagementMutation<{ rateId: string }>((email, p) =>
    api.delete(`/api/management/${enc(email)}/rate/${p.rateId}`));

// F-188 — year < currentYear (M-167)
export interface SalaryHistoryPayload { year: number; role: string; annual: number; note: string | null }
export const useSaveSalaryHistory = () =>
  useManagementMutation<SalaryHistoryPayload>((email, p) => api.put(`/api/management/${enc(email)}/salary-history`, p));
export const useDeleteSalaryHistory = () =>
  useManagementMutation<{ year: number }>((email, p) =>
    api.delete(`/api/management/${enc(email)}/salary-history/${p.year}`));

// F-189 — forces Gallup + Nova de la cible (M-168 ; audit serveur NT-L10-03)
export const useSetStrengths = () =>
  useManagementMutation<EmployeeStrengths>((email, p) => api.put(`/api/management/${enc(email)}/strengths`, p));

// F-191
export interface ObjectivePayload {
  period: 'annual' | 'q1' | 'q2' | 'q3' | 'q4';
  year: number;
  title: string;
  status?: string;
  targetDate: string | null;
}
export const useCreateObjective = () =>
  useManagementMutation<ObjectivePayload>((email, p) => api.post(`/api/management/${enc(email)}/objective`, p));
export const useUpdateObjectiveStatus = () =>
  useManagementMutation<{ objectiveId: string; status: string }>((email, p) =>
    api.patch(`/api/management/${enc(email)}/objective/${p.objectiveId}`, { status: p.status }));
export const useDeleteObjective = () =>
  useManagementMutation<{ objectiveId: string }>((email, p) =>
    api.delete(`/api/management/${enc(email)}/objective/${p.objectiveId}`));

// F-192 — check-in (M-767 re-submit = update ; M-171 ratings bornes serveur)
export interface CheckinPayload { year: number; quarter: number; note: string | null; ratings: Record<string, number> }
export const useSubmitCheckin = () =>
  useManagementMutation<CheckinPayload>((email, p) => api.post(`/api/management/${enc(email)}/checkin`, p));

// F-190 — documents employe (lot UPLOADS)
export function useUploadEmployeeDocument(email: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; kind: 'nova' | 'gallup' | 'autre' }) => {
      const { uploadGestFile } = await import('../lib/blobs/uploadFile');
      return uploadGestFile<{ id: string }>(
        `/api/management/${enc(email)}/documents`,
        input.file,
        { kind: input.kind },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(email) });
    },
  });
}

export function useDeleteEmployeeDocument(email: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.delete<{ ok: boolean }>(`/api/management/${enc(email)}/documents/${documentId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(email) });
    },
  });
}
